import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, Loader2, ShieldCheck, CheckCircle2, XCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// ---------------------------------------------------------------------------
// Change Password — authenticated self-service.
// Any signed-in user can update their own credentials without an admin reset.
// ---------------------------------------------------------------------------
const MIN_LEN = 8;

function passwordChecks(pw) {
  return {
    length:  pw.length >= MIN_LEN,
    letter:  /[A-Za-z]/.test(pw),
    number:  /[0-9]/.test(pw),
    special: /[^A-Za-z0-9]/.test(pw),
  };
}

export default function ChangePassword() {
  const { user } = useAuth();

  const [form, setForm] = useState({ old_password: "", new_password: "", confirm: "" });
  const [show, setShow] = useState({ old: false, next: false, confirm: false });
  const [busy, setBusy] = useState(false);

  const checks = useMemo(() => passwordChecks(form.new_password), [form.new_password]);
  const allGreen = checks.length && checks.letter && checks.number;
  const sameAsOld = form.new_password && form.new_password === form.old_password;
  const mismatch = form.confirm && form.new_password !== form.confirm;
  const canSubmit =
    !!form.old_password && allGreen && !sameAsOld && !mismatch && !!form.confirm && !busy;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        old_password: form.old_password,
        new_password: form.new_password,
      });
      toast.success("Password updated. Use the new one next time you sign in.");
      setForm({ old_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="change-password-root">
      <header>
        <h2 className="text-[16px] md:text-[18px] font-bold text-[#1d3557]">Change Password</h2>
        <p className="text-[12.5px] text-[#667085] mt-0.5">
          Update the password you use to sign in. Signed in as{" "}
          <span className="notranslate font-semibold text-[#1d3557]">{user?.email}</span>.
        </p>
      </header>

      <form onSubmit={submit} className="bg-white rounded-xl border border-[#E4E7EC] overflow-hidden" data-testid="change-password-form">
        <div className="p-4 border-b border-[#F1FAEE] flex items-center gap-2">
          <div className="w-9 h-9 rounded-md grid place-items-center bg-[#F1FAEE] text-[#1d3557]">
            <KeyRound size={16} />
          </div>
          <div>
            <h3 className="text-[13.5px] font-bold text-[#1d3557]">Update password</h3>
            <p className="text-[12px] text-[#667085] mt-0.5">
              You&apos;ll receive an email confirmation once the change is saved.
            </p>
          </div>
        </div>

        <div className="p-4 space-y-4">
          <PasswordField
            label="Current password"
            value={form.old_password}
            onChange={(v) => setForm({ ...form, old_password: v })}
            visible={show.old}
            onToggle={() => setShow({ ...show, old: !show.old })}
            autoComplete="current-password"
            testId="cp-old"
            footer={
              <Link
                to="/forgot-password"
                data-testid="cp-forgot-link"
                className="text-[11.5px] text-[#457b9d] hover:text-[#e63946] font-semibold"
              >
                Forgot current password?
              </Link>
            }
          />

          <div>
            <PasswordField
              label="New password"
              value={form.new_password}
              onChange={(v) => setForm({ ...form, new_password: v })}
              visible={show.next}
              onToggle={() => setShow({ ...show, next: !show.next })}
              autoComplete="new-password"
              testId="cp-new"
              error={sameAsOld ? "Choose a password different from your current one." : null}
            />
            <ChecklistRow ok={checks.length}  label={`At least ${MIN_LEN} characters`} />
            <ChecklistRow ok={checks.letter}  label="Contains a letter" />
            <ChecklistRow ok={checks.number}  label="Contains a number" />
            <ChecklistRow ok={checks.special} label="Special character (recommended)" optional />
          </div>

          <PasswordField
            label="Confirm new password"
            value={form.confirm}
            onChange={(v) => setForm({ ...form, confirm: v })}
            visible={show.confirm}
            onToggle={() => setShow({ ...show, confirm: !show.confirm })}
            autoComplete="new-password"
            testId="cp-confirm"
            error={mismatch ? "Passwords don't match." : null}
          />
        </div>

        <div className="px-4 py-3 bg-[#F7F9FC] border-t border-[#F1FAEE] flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 text-[11.5px] text-[#667085]">
            <ShieldCheck size={13} className="text-[#457b9d]" />
            Passwords are hashed with bcrypt and never emailed back to you.
          </div>
          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="cp-submit"
            className="h-10 px-5 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold disabled:opacity-40 inline-flex items-center gap-1.5"
          >
            {busy
              ? <><Loader2 size={13} className="animate-spin" /> Updating…</>
              : <><KeyRound size={13} /> Update password</>}
          </button>
        </div>
      </form>
    </div>
  );
}

// -------------------- Sub-components --------------------

function PasswordField({ label, value, onChange, visible, onToggle, autoComplete, testId, footer, error }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-[#667085] mb-1">
        {label}
      </div>
      <div className="relative">
        <input
          type={visible ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          data-testid={`${testId}-input`}
          className={`w-full h-10 rounded-md border px-3 pr-10 text-[13px] outline-none focus:border-[#457b9d] ${
            error ? "border-[#e63946]" : "border-[#E4E7EC]"
          }`}
        />
        <button
          type="button"
          onClick={onToggle}
          data-testid={`${testId}-toggle`}
          aria-label={visible ? "Hide password" : "Show password"}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 grid place-items-center text-[#98A2B3] hover:text-[#1d3557]"
        >
          {visible ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
      <div className="flex items-center justify-between gap-2 mt-1 min-h-[16px]">
        {error
          ? <div className="text-[11px] text-[#e63946]" data-testid={`${testId}-error`}>{error}</div>
          : <div />}
        {footer || <div />}
      </div>
    </label>
  );
}

function ChecklistRow({ ok, label, optional }) {
  const Icon = ok ? CheckCircle2 : XCircle;
  const cls = ok
    ? "text-[#16a34a]"
    : optional ? "text-[#a8dadc]" : "text-[#e63946]";
  return (
    <div className={`text-[11.5px] mt-1 flex items-center gap-1.5 ${cls}`}>
      <Icon size={12} /> {label}
    </div>
  );
}
